/**
 * af1c cart page — snappy qty updates with optimistic UI + diff-render server sync.
 *
 * Pattern (from emiley-launch v12, per project wiki):
 *   1. Click "+" / "−" / remove → update DOM immediately (feels ~instant, <5ms).
 *   2. Queue the /cart/change.js POST in the background.
 *   3. Coalesce rapid clicks on the same line into one final request (last-wins).
 *   4. When server responds, compare our optimistic subtotal against cart.total_price.
 *      If they match → nothing to do (skip re-render). If they diverge → reconcile
 *      that one line's numbers, do NOT rebuild the whole DOM (rebuild kills the button).
 *   5. Empty cart → replace the wrap contents inline; never window.location.reload().
 */
(function () {
  'use strict';

  // Tag the <html> so mobile-scroll CSS can win without :has() support.
  // Chrome iOS 16 / older iOS Safari drop :has() against html/body.
  document.documentElement.classList.add('template-cart-html');

  const $wrap = document.querySelector('.cart-wrap');
  const $lines = document.getElementById('cart-lines');
  if (!$lines) return;

  const money = (cents) => '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  const cents = (dollarText) => Math.round(parseFloat(String(dollarText).replace(/[^0-9.]/g, '')) * 100) || 0;

  // Per-line request coalescing: only the newest quantity for each key is in flight.
  const pending = new Map(); // key -> {targetQty, controller}

  function updateChromeCount(count) {
    document.querySelectorAll('[data-cart-count], .cart-count').forEach((el) => {
      el.textContent = String(count);
    });
  }

  function updateSubtotal(totalCents) {
    document.querySelectorAll('[data-cart-subtotal], [data-cart-total]').forEach((el) => {
      el.textContent = money(totalCents);
    });
  }

  function computeOptimisticSubtotal() {
    let sum = 0;
    $lines.querySelectorAll('.cart-line').forEach((line) => {
      const unit = parseInt(line.dataset.unitPrice, 10) || 0;
      const qty = parseInt(line.querySelector('[data-qty-input]')?.value, 10) || 0;
      sum += unit * qty;
    });
    return sum;
  }

  // Guard so double-fires (JS optimistic + server confirm) don't restart
  // the fade or re-append duplicate empty markup.
  let emptyRendered = false;
  function renderEmpty() {
    if (!$wrap || emptyRendered) return;
    emptyRendered = true;

    const $form = $wrap.querySelector('.cart-form');
    const $head = $wrap.querySelector('.cart-head');

    const emptyHTML = `
      <div class="cart-empty">
        <p class="cart-empty__msg">no shirts yet.</p>
        <a class="cart-empty__cta" href="/">start shopping</a>
      </div>`;

    // Fade out the current form (line items + checkout) then swap in the
    // empty state. The empty state has its own CSS keyframe fade-in, so the
    // two motions feel like one continuous crossfade.
    if ($form) {
      $form.style.transition = 'opacity 200ms ease-out, transform 200ms ease-out';
      $form.style.opacity = '0';
      $form.style.transform = 'translateY(-4px)';
      setTimeout(() => {
        if ($head) $head.remove();          // headline is redundant with the empty block
        $form.remove();
        $wrap.insertAdjacentHTML('beforeend', emptyHTML);
        updateChromeCount(0);
      }, 200);
    } else {
      // Called without a form present (edge case) — just render.
      $wrap.innerHTML = emptyHTML;
      updateChromeCount(0);
    }
  }

  async function syncLine(key) {
    const state = pending.get(key);
    if (!state) return;
    const targetQty = state.targetQty;

    // Cancel any earlier in-flight request for this line
    if (state.controller) state.controller.abort();
    const controller = new AbortController();
    state.controller = controller;

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: targetQty }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('cart update failed: ' + res.status);
      const cart = await res.json();

      // If another click happened while we waited, that click's own syncLine
      // will run — bail so we don't clobber its target.
      if (pending.get(key)?.targetQty !== targetQty) return;
      pending.delete(key);

      // Empty cart? render inline, don't reload.
      if (cart.item_count === 0) {
        renderEmpty();
        return;
      }

      // Update chrome count from server truth.
      updateChromeCount(cart.item_count);

      // Diff-render: if our optimistic subtotal already matches, do NOTHING.
      // This is the whole trick — no DOM rebuild = no flicker = button still feels alive.
      const optimistic = computeOptimisticSubtotal();
      if (optimistic === cart.total_price) return;

      // Divergence: quietly reconcile just this line's numbers + subtotal.
      const line = $lines.querySelector(`.cart-line[data-key="${CSS.escape(key)}"]`);
      const found = cart.items.find((i) => i.key === key);
      if (found && line) {
        const priceEl = line.querySelector('.cart-line__price');
        if (priceEl) priceEl.textContent = money(found.final_line_price);
        const qtyInput = line.querySelector('[data-qty-input]');
        if (qtyInput && parseInt(qtyInput.value, 10) !== found.quantity) {
          qtyInput.value = found.quantity;
        }
      }
      updateSubtotal(cart.total_price);
    } catch (err) {
      if (err.name === 'AbortError') return; // superseded, fine
      console.error('[af1c-cart] sync failed', err);
      // Surface a minimal visual — the badge stays but numbers may lag until next click.
      const line = $lines.querySelector(`.cart-line[data-key="${CSS.escape(key)}"]`);
      if (line) line.classList.add('is-error');
    }
  }

  // Apply a new target quantity optimistically, then schedule a sync.
  function setLineQuantity(key, nextQty) {
    const line = $lines.querySelector(`.cart-line[data-key="${CSS.escape(key)}"]`);
    if (!line) return;

    nextQty = Math.max(0, nextQty | 0);
    const unit = parseInt(line.dataset.unitPrice, 10) || 0;

    // Optimistic DOM: qty, this line's price, subtotal, and chrome pill.
    const qtyInput = line.querySelector('[data-qty-input]');
    if (qtyInput) qtyInput.value = nextQty;

    if (nextQty === 0) {
      // Smooth collapse:
      //  1) Measure current height and lock it as inline max-height so the
      //     transition has a known starting value (max-height can't animate
      //     from `none`).
      //  2) Force a reflow so the browser accepts step 1 as a state.
      //  3) Add the .is-removing class — CSS then transitions max-height,
      //     margin, padding, opacity, and transform in lockstep.
      //  4) On transitionend (or a safety timeout) remove the node.
      // Also collapse the flex gap slot by zeroing the line's own margin.
      const h = line.getBoundingClientRect().height;
      line.style.maxHeight = h + 'px';
      // Force reflow so max-height:<h>px is registered as the transition start.
      // eslint-disable-next-line no-unused-expressions
      line.offsetHeight;
      requestAnimationFrame(() => {
        line.classList.add('is-removing');
      });
      let removed = false;
      const doRemove = () => {
        if (removed) return;
        removed = true;
        line.remove();
      };
      line.addEventListener('transitionend', (ev) => {
        // Only remove after the max-height transition completes — not opacity
        // (which finishes first and would remove the node before it collapses).
        if (ev.propertyName === 'max-height') doRemove();
      });
      // Safety fallback if transitionend never fires. Slightly longer than
      // the 380ms max-height transition to allow for jitter.
      setTimeout(doRemove, 550);
    } else {
      const priceEl = line.querySelector('.cart-line__price');
      if (priceEl) priceEl.textContent = money(unit * nextQty);
    }

    // Optimistic totals from post-mutation DOM.
    // Wait a tick so a removed line is out of the tree.
    setTimeout(() => {
      const optimisticSub = computeOptimisticSubtotal();
      updateSubtotal(optimisticSub);

      // Optimistic chrome count = sum of all remaining qtys.
      let count = 0;
      $lines.querySelectorAll('.cart-line:not(.is-removing) [data-qty-input]').forEach((i) => {
        count += parseInt(i.value, 10) || 0;
      });
      updateChromeCount(count);

      // If DOM says empty, render empty state immediately (server confirms later).
      if (count === 0) renderEmpty();
    }, 0);

    // Coalesce: newest wins. Preserve any in-flight controller so syncLine can abort it.
    const prev = pending.get(key);
    if (prev?.debounce) clearTimeout(prev.debounce);
    const state = {
      targetQty: nextQty,
      controller: prev?.controller || null,
      debounce: null,
    };
    // Debounce a tick so rapid + + + becomes one call with the final number.
    state.debounce = setTimeout(() => syncLine(key), 120);
    pending.set(key, state);
  }

  // Delegated click handling
  $lines.addEventListener('click', (e) => {
    const btn = e.target.closest('.qty-btn');
    if (btn) {
      e.preventDefault();
      const stepper = btn.closest('.qty-stepper');
      if (!stepper) return;
      const key = stepper.dataset.key;
      const input = stepper.querySelector('[data-qty-input]');
      const cur = parseInt(input?.value, 10) || 0;
      const next = btn.dataset.qty === 'up' ? cur + 1 : cur - 1;
      setLineQuantity(key, next);
      return;
    }
    const rm = e.target.closest('[data-remove]');
    if (rm) {
      e.preventDefault();
      setLineQuantity(rm.dataset.key, 0);
    }
  });

  // Manual qty input (typing) — debounced longer than button clicks.
  let inputTimer;
  $lines.addEventListener('input', (e) => {
    const inp = e.target.closest('[data-qty-input]');
    if (!inp) return;
    clearTimeout(inputTimer);
    const stepper = inp.closest('.qty-stepper');
    const key = stepper?.dataset.key;
    if (!key) return;
    inputTimer = setTimeout(() => {
      setLineQuantity(key, parseInt(inp.value, 10) || 0);
    }, 500);
  });
})();
